import { existsSync } from "node:fs";
import { dirname } from "node:path";

import {
  CHATGPT_APP_PATHS,
  CLAUDE_INSTALL_PATHS,
  CLAUDE_MCP_PATHS,
  CODEX_CONFIG_PATHS,
  CODEX_INSTALL_PATHS,
  DEEPSEEK_HARNESS_INSTALL_PATHS,
  DEEPSEEK_HARNESS_PROFILE_PATH,
  CURSOR_INSTALL_PATHS,
  CURSOR_MCP_PATHS,
  OPENCLAW_CONFIG_PATHS,
  OPENCLAW_INSTALL_PATHS,
  VSCODE_INSTALL_PATHS,
  VSCODE_MCP_PATHS,
  WINDSURF_INSTALL_PATHS,
  WINDSURF_MCP_PATHS,
  ZED_INSTALL_PATHS,
  ZED_SETTINGS_PATHS,
} from "../constants.js";
import type { SurfaceName } from "./names.js";

export interface SurfaceLocator {
  configPaths: string[];
  installPaths: string[];
}

export interface SurfaceDetection {
  detected: boolean;
  preferredPath?: string;
  existingPath?: string;
  evidence: string[];
}

type Exists = (path: string) => boolean;

const SURFACE_LOCATORS: Record<SurfaceName, SurfaceLocator> = {
  claude: {
    configPaths: CLAUDE_MCP_PATHS,
    installPaths: CLAUDE_INSTALL_PATHS,
  },
  cursor: {
    configPaths: CURSOR_MCP_PATHS,
    installPaths: CURSOR_INSTALL_PATHS,
  },
  codex: {
    configPaths: CODEX_CONFIG_PATHS,
    installPaths: CODEX_INSTALL_PATHS,
  },
  deepseek: {
    configPaths: [DEEPSEEK_HARNESS_PROFILE_PATH],
    installPaths: DEEPSEEK_HARNESS_INSTALL_PATHS,
  },
  openclaw: {
    configPaths: OPENCLAW_CONFIG_PATHS,
    installPaths: OPENCLAW_INSTALL_PATHS,
  },
  vscode: {
    configPaths: VSCODE_MCP_PATHS,
    installPaths: VSCODE_INSTALL_PATHS,
  },
  windsurf: {
    configPaths: WINDSURF_MCP_PATHS,
    installPaths: WINDSURF_INSTALL_PATHS,
  },
  zed: {
    configPaths: ZED_SETTINGS_PATHS,
    installPaths: ZED_INSTALL_PATHS,
  },
  chatgpt: {
    configPaths: [],
    installPaths: CHATGPT_APP_PATHS,
  },
};

function firstExistingPath(paths: string[], exists: Exists): string | undefined {
  return paths.find((path) => exists(path));
}

function buildFallbackInstallPaths(configPaths: string[]): string[] {
  return [...new Set(configPaths.map((path) => dirname(path)))];
}

export function getSurfaceLocator(name: SurfaceName): SurfaceLocator {
  return SURFACE_LOCATORS[name];
}

export function detectSurface(name: SurfaceName, exists: Exists = existsSync): SurfaceDetection {
  const locator = getSurfaceLocator(name);
  const existingConfigPath = firstExistingPath(locator.configPaths, exists);
  const preferredPath = locator.configPaths[0] ?? locator.installPaths[0];
  const installCandidates = [
    ...locator.installPaths,
    ...buildFallbackInstallPaths(locator.configPaths),
  ];
  const existingInstallPath = firstExistingPath(installCandidates, exists);
  const existingPath = existingConfigPath ?? existingInstallPath;
  const evidence: string[] = [];

  if (existingConfigPath) {
    evidence.push(`config found: ${existingConfigPath}`);
  }
  if (existingInstallPath && existingInstallPath !== existingConfigPath) {
    evidence.push(`install marker found: ${existingInstallPath}`);
  }
  if (!existingConfigPath && !existingInstallPath) {
    evidence.push("no candidate paths found on disk");
  }

  const detection: SurfaceDetection = {
    detected: Boolean(existingConfigPath || existingInstallPath),
    evidence,
  };

  if (preferredPath) {
    detection.preferredPath = preferredPath;
  }
  if (existingPath) {
    detection.existingPath = existingPath;
  }

  return detection;
}
